import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import FormField from "../shared/FormField";
import Button from "../shared/Button";
import "../../styles/WifiQrPage.css";
import { motion, AnimatePresence } from "framer-motion";
import usePagination from "../../hooks/usePagination";
import Pagination from "../shared/Pagination";
import useWifiActions from "../../hooks/useWifiActions";
import WifiEntry from "../shared/WifiEntry";
import useQRCodeDownloader from "../../hooks/useQRCodeDownloader";
import Logo from "../../assets/images/Wi-Fy-Logo.svg";

const WifiQrPage: React.FC = () => {
  const [name, setName] = useState("");
  const [ssid, setSsid] = useState("");
  const [pwd, setPwd] = useState("");
  const [enc, setEnc] = useState<"WPA2" | "WPA3">("WPA2");

  const { wifiList, fetchWifiList, createWifi, deleteWifi } = useWifiActions();
  const { handleDownloadQRCode } = useQRCodeDownloader(wifiList);
  const isFetchedRef = useRef(false);

  useEffect(() => {
    if (!isFetchedRef.current) {
      fetchWifiList();
      isFetchedRef.current = true;
    }
  }, [fetchWifiList]);

  const handleSave = async () => {
    const trimmedSsid = ssid.trim();
    const trimmedName = name.trim();
    if (!trimmedSsid || !trimmedName) return;

    const created = await createWifi({
      name: trimmedName,
      ssid: trimmedSsid,
      password: pwd,
      encryptionType: enc,
    });
    if (created) {
      setName("");
      setSsid("");
      setPwd("");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  const handleDelete = async (id: string, name: string) => {
    await deleteWifi(id, name);
  };

  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    startIdx,
    endIdx,
    paginatedEntries,
    totalItems,
  } = usePagination(Object.entries(wifiList), 10);

  return (
    <div className="my-wifi-container">
      {/* Formular oben */}
      <div className="wifi-form-wrapper">
        <div className="wifi-form-card">
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              marginBottom: "18px",
            }}
          >
            <div
              className="linktree-empty"
              style={{
                margin: 0,
                flex: 1,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h3>Create your WiFi QR Code</h3>
                <p>Generate a QR code to easily share your WiFi credentials.</p>
              </div>

              <img
                src={Logo}
                alt="WiFi Logo"
                style={{
                  width: "56px",
                  height: "auto",
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <FormField
              label="Name"
              name="wifi-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter a name for this WiFi"
              required
            />
            <FormField
              label="SSID"
              name="wifi-ssid"
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
              placeholder="Enter your WiFi network name"
              required
              autoComplete="new-password"
            />
            <FormField
              label="Password"
              name="wifi-password"
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Enter WiFi password"
              required
              autoComplete="new-password"
            />
            <FormField
              label="Encryption"
              name="wifi-encryption"
              type="select"
              value={enc}
              onChange={(e) => setEnc(e.target.value as "WPA2" | "WPA3")}
              options={[
                { label: "WPA2", value: "WPA2" },
                { label: "WPA3", value: "WPA3" },
              ]}
            />
            <div className="wifi-button-wrapper">
              <Button variant="primary" type="submit">
                Create & Save
              </Button>
            </div>
          </form>
        </div>
      </div>
      <div className="wifi-info-notice">
        <div className="wifi-info-content">
          <svg
            className="wifi-info-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>
            For compliance and data protection info, please refer to our{" "}
            <Link to="/privacy" className="wifi-privacy-link">
              Privacy Notice
            </Link>
            .
          </span>
        </div>
      </div>

      <div className="wifi-list">
        <AnimatePresence>
          {paginatedEntries.map(([id, wifi]) => (
            <motion.div
              key={id}
              initial={{ opacity: 0, y: 36 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 36 }}
              transition={{ duration: 0.33, ease: "easeOut" }}
              layout
            >
              <WifiEntry
                id={wifi.id}
                name={wifi.name}
                ssid={wifi.ssid}
                encryptionType={wifi.encryptionType}
                qrString={wifi.qrString || ""}
                onDelete={() => handleDelete(wifi.id, wifi.name)}
                onDownload={handleDownloadQRCode}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="pagination-container">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          setCurrentPage={setCurrentPage}
          startIdx={startIdx}
          endIdx={endIdx}
          totalItems={totalItems}
        />
      </div>
    </div>
  );
};

export default WifiQrPage;
